import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Sun, Moon, ChevronDown, LogOut, User as UserIcon } from "lucide-react";
import { useUser } from "@foundation/altzero_base/base/contextapi/UserContext";
import { theme } from "../theme";
import { useTheme } from "../contexts/ThemeContext";
import { usePluginNavigation } from "../plugins/hooks";
import { useAdminAuth } from "@foundation/altzero_base/base/hooks/useAdminAuth";
import { supabase } from "@foundation/altzero_base/base/utils/supabaseClient";

const Header = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { theme: currentTheme, toggleTheme } = useTheme();
  const userMenuRef = useRef<HTMLDivElement>(null);

  const { navigation: pluginNavigation } = usePluginNavigation();
  const { isAdmin } = useAdminAuth();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setUserMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  const isActive = (path: string) => {
    if (path === "/settings") {
      return (
        location.pathname.startsWith("/settings") ||
        location.pathname.startsWith("/organizations") ||
        location.pathname.startsWith("/teams") ||
        location.pathname.startsWith("/subscription")
      );
    }
    if (path === "/admin") {
      return location.pathname.startsWith("/admin");
    }
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };

  const staticNavigation = [
    { name: "Settings", route: "/settings", order: 10 },
  ];

  if (isAdmin) {
    staticNavigation.push({ name: "Dashboard", route: "/analytics", order: 0 });
    staticNavigation.push({ name: "Admin", route: "/admin", order: 11 });
  }

  // Filter admin-only plugin routes for non-admin users
  const adminOnlyRoutes = ['/knowledge'];
  const filteredPluginNav = isAdmin
    ? pluginNavigation
    : pluginNavigation.filter((item) => !adminOnlyRoutes.includes(item.route));

  const seen = new Set<string>();
  const allNavigation = [...filteredPluginNav, ...staticNavigation]
    .filter((item) => {
      if (seen.has(item.route)) return false;
      seen.add(item.route);
      return true;
    })
    .sort((a, b) => (a.order ?? 999) - (b.order ?? 999));

  const userInitial = user?.email?.charAt(0).toUpperCase() || "U";
  const userDisplayName = user?.email?.split("@")[0];

  return (
    <header className={theme.nav.bar}>
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex items-center gap-8">
            <Link to="/" className="flex items-center flex-shrink-0">
              <img
                src="/charlie-logo-light.png"
                alt="Charlie"
                className="h-10 w-auto"
              />
            </Link>

            {/* Desktop nav */}
            {user && (
              <nav className="hidden md:block" aria-label="Main navigation">
                <ul className="flex items-center gap-1">
                  {allNavigation.map((item) => (
                    <li key={item.route}>
                      <Link
                        to={item.route}
                        className={isActive(item.route) ? theme.nav.linkActive : theme.nav.linkIdle}
                      >
                        {item.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>
            )}
          </div>

          {/* Right side controls */}
          <div className="flex items-center gap-2">
            {/* Theme toggle */}
            <button
              onClick={toggleTheme}
              title={currentTheme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              aria-label={currentTheme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              className="flex items-center justify-center w-8 h-8 rounded-xl dark:text-white/50 text-white/50 dark:hover:text-white/80 hover:text-white/80 dark:hover:bg-white/[0.07] hover:bg-white/[0.07] transition-all duration-200"
            >
              {currentTheme === "dark" ? (
                <Sun className="w-4 h-4" />
              ) : (
                <Moon className="w-4 h-4" />
              )}
            </button>

            {/* User menu — desktop */}
            {user ? (
              <div className="hidden md:block relative" ref={userMenuRef}>
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className={theme.nav.userMenu}
                  aria-haspopup="true"
                  aria-expanded={userMenuOpen}
                >
                  <div className="w-7 h-7 rounded-full dark:bg-sy-teal/25 bg-sy-teal/25 dark:text-sy-teal text-sy-teal flex items-center justify-center text-xs font-bold flex-shrink-0">
                    {userInitial}
                  </div>
                  <span className="text-sm font-medium dark:text-white/85 text-white/85 max-w-[120px] truncate">
                    {userDisplayName}
                  </span>
                  <ChevronDown
                    className={`w-3.5 h-3.5 dark:text-white/40 text-white/40 transition-transform duration-200 flex-shrink-0 ${
                      userMenuOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {userMenuOpen && (
                  <div className={theme.nav.dropdown}>
                    <div className="px-3 pt-3 pb-2 dark:border-white/[0.07] border-b border-white/[0.07]">
                      <p className="text-xs dark:text-white/40 text-white/40 truncate">{user.email}</p>
                    </div>
                    <div className="py-1.5">
                      <Link
                        to="/profile"
                        className={theme.nav.dropdownItem}
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <div className="flex items-center gap-2.5">
                          <UserIcon className="w-3.5 h-3.5 dark:text-white/40 text-gray-400" />
                          Profile
                        </div>
                      </Link>
                      <button
                        onClick={() => {
                          handleLogout();
                          setUserMenuOpen(false);
                        }}
                        className="w-full text-left px-4 py-2.5 text-sm text-red-400 dark:hover:bg-white/[0.07] hover:bg-white/[0.07] transition-all duration-150 flex items-center gap-2.5"
                      >
                        <LogOut className="w-3.5 h-3.5" />
                        Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="hidden md:block">
                <Link to="/login" className={theme.button.primaryTeal}>
                  Login
                </Link>
              </div>
            )}

            {/* Mobile menu toggle */}
            <button
              className="md:hidden flex items-center justify-center w-8 h-8 rounded-xl dark:text-white/70 text-white/70 dark:hover:bg-white/[0.07] hover:bg-white/[0.07] transition-all duration-200"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileMenuOpen}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d={mobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"}
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className={`md:hidden dark:bg-sy-navy/98 bg-[#141139]/98 dark:border-white/[0.08] border-t border-white/[0.08]`}>
          <div className="container mx-auto px-4 py-4 space-y-1">
            {user ? (
              <>
                {/* User info */}
                <div className="flex items-center gap-3 px-4 py-3 mb-2 dark:bg-white/[0.04] bg-white/[0.04] rounded-xl">
                  <div className="w-8 h-8 rounded-full dark:bg-sy-teal/25 bg-sy-teal/25 dark:text-sy-teal text-sy-teal flex items-center justify-center text-sm font-bold flex-shrink-0">
                    {userInitial}
                  </div>
                  <p className="text-sm dark:text-white/75 text-white/75 truncate">{user.email}</p>
                </div>

                {allNavigation.map((item) => (
                  <Link
                    key={`mobile-${item.route}`}
                    to={item.route}
                    className={isActive(item.route) ? theme.nav.mobileLinkActive : theme.nav.mobileLinkIdle}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                ))}

                <Link
                  to="/profile"
                  className={isActive("/profile") ? theme.nav.mobileLinkActive : theme.nav.mobileLinkIdle}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Profile
                </Link>

                <div className={`pt-2 border-t dark:border-white/[0.08] border-white/[0.08]`}>
                  <button
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="flex items-center gap-2 w-full py-2.5 px-4 rounded-xl text-red-400 dark:hover:bg-white/[0.06] hover:bg-white/[0.06] transition-colors duration-150 text-sm font-medium"
                  >
                    <LogOut className="w-4 h-4" />
                    Logout
                  </button>
                </div>
              </>
            ) : (
              <Link
                to="/login"
                className={theme.nav.mobileLinkActive}
                onClick={() => setMobileMenuOpen(false)}
              >
                Login
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
